import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, Paperclip } from "lucide-react";
import { useNavigate } from "react-router-dom";
import i18n from "../i18n";

const ResumeForm = () => {
  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [langTick, setLangTick] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onLang = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const tr = useMemo(() => {
    void langTick;

    return {
      back: i18n.t("resumeForm.back"),
      title: i18n.t("resumeForm.title"),

      success: {
        title: i18n.t("resumeForm.success.title"),
        text: i18n.t("resumeForm.success.text"),
      },

      fields: {
        position: i18n.t("resumeForm.fields.position"),
        firstName: i18n.t("resumeForm.fields.firstName"),
        lastName: i18n.t("resumeForm.fields.lastName"),
        email: i18n.t("resumeForm.fields.email"),
        phone: i18n.t("resumeForm.fields.phone"),
        resume: i18n.t("resumeForm.fields.resume"),
      },

      placeholders: {
        chooseFile: i18n.t("resumeForm.placeholders.chooseFile"),
      },

      options: {
        selectPosition: i18n.t("resumeForm.options.selectPosition"),
        frontend: i18n.t("resumeForm.options.frontend"),
        backend: i18n.t("resumeForm.options.backend"),
        designer: i18n.t("resumeForm.options.designer"),
      },

      actions: {
        send: i18n.t("resumeForm.actions.send"),
      },

      errors: {
        submissionFailed: i18n.t("resumeForm.errors.submissionFailed"),
        generic: i18n.t("resumeForm.errors.generic"),
      },
    };
  }, [langTick]);

  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      setResumeFile(event.target.files[0]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    if (resumeFile) {
      formData.append("resume", resumeFile);
    }

    try {
      const response = await fetch("/Backend/api/resumes.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.text();
      console.log("✅ Server responded:", result);

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert(`❌ ${tr.errors.submissionFailed}: ${result}`);
      }
    } catch (error) {
      console.error("❌ Error during submission:", error);
      alert(`❌ ${tr.errors.generic}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B2A]">
      <div className="max-w-7xl mx-auto py-12 px-6 mb-20">
        <div
          className="flex items-center text-gray-200 cursor-pointer hover:text-white self-start mb-6 max-w-7xl w-full"
          onClick={() => navigate(-1)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate(-1);
          }}
        >
          <ArrowLeft size={20} />
          <span className="ml-2">{tr.back}</span>
        </div>

        {isSubmitted ? (
          <div className="bg-white shadow-md rounded-xl p-12 max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-green-600 mb-4">
              {tr.success.title}
            </h2>
            <p className="text-gray-700 text-lg">{tr.success.text}</p>
          </div>
        ) : (
          <>
            <h2 className="text-4xl font-bold text-white mb-8 text-center">
              {tr.title}
            </h2>

            <div className="bg-white shadow-md rounded-xl p-8 max-w-3xl w-full mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium">
                    {tr.fields.position}
                  </label>

                  <div className="relative">
                    <select
                      name="position"
                      className="w-full p-3 border border-gray-300 rounded-md mt-1 appearance-none pr-10"
                      required
                    >
                      <option value="">{tr.options.selectPosition}</option>
                      <option value="frontend">{tr.options.frontend}</option>
                      <option value="backend">{tr.options.backend}</option>
                      <option value="designer">{tr.options.designer}</option>
                    </select>

                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <ChevronDown size={20} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      {tr.fields.firstName}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className="w-full p-3 border border-gray-300 rounded-md mt-1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">
                      {tr.fields.lastName}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className="w-full p-3 border border-gray-300 rounded-md mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      {tr.fields.email}
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full p-3 border border-gray-300 rounded-md mt-1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">
                      {tr.fields.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full p-3 border border-gray-300 rounded-md mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium">
                    {tr.fields.resume}
                  </label>

                  <label className="flex items-center gap-2 border border-gray-300 rounded-md p-3 cursor-pointer mt-1">
                    <Paperclip size={20} className="text-gray-500" />
                    <span className="text-gray-500">
                      {tr.placeholders.chooseFile}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      required
                    />
                  </label>

                  {resumeFile && (
                    <p className="text-sm text-gray-600 mt-1">{resumeFile.name}</p>
                  )}
                </div>

                <div className="flex justify-center">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white py-3 px-10 rounded-full hover:bg-blue-700 transition"
                  >
                    {tr.actions.send}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResumeForm;









